import {
  ExBadge, BadgeColor, BadgeShape, BadgeSize,
  ExTooltip,
  TooltipAlignment,
  TooltipPosition,
  ButtonFlavor,
  ButtonType,
  ExDropdown,
  ExMenu,
  ExMenuItem,
  MenuItemVariant,
} from '@boomi/exosphere';
import './ListViewUtils.scss';
import dropdownRefHandler from '@components/ToolManagement/helpers/dropdownRefHandler';

/**
 * Returns the ordinal suffix for a given day number.
 * @param day - The day of the month.
 * @returns The ordinal suffix (e.g., 'st', 'nd', 'rd', 'th').
 */
export const getDaySuffix = (day: number): string => {
  if (day % 10 === 1 && day !== 11) return 'st';
  if (day % 10 === 2 && day !== 12) return 'nd';
  if (day % 10 === 3 && day !== 13) return 'rd';
  return 'th';
};

/**
 * Formats a date string into "day<suffix> MMM YYYY, HH:mm:ss" format.
 * Example: "14th Jul 2025, 13:45:30"
 * @param dateString - The date string to format.
 * @returns The formatted date string.
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate();
  const daySuffix = getDaySuffix(day);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  return `${day}${daySuffix} ${new Intl.DateTimeFormat('en-US', options).format(
    date,
  )}`;
};
export const sourceStatus = ({ status }: { status: string }) => {
  let badgeColor = BadgeColor.GRAY;
  let badgeLabel = status;

  if (status === 'STALE') {
    badgeColor = BadgeColor.YELLOW;
    badgeLabel = 'Stale';
  } else if (status === 'ACTIVE') {
    badgeColor = BadgeColor.GREEN;
    badgeLabel = 'Active';
  } else if (status === 'NOT_AVAILABLE') {
    badgeLabel = 'Not Available';
  } else if (status === 'DISABLED') {
    badgeLabel = 'Disabled';
  }

  return (
    <ExBadge
      color={badgeColor}
      shape={BadgeShape.SQUARED}
      size={BadgeSize.SMALL}
    >
      {badgeLabel}
    </ExBadge>
  );
};

export const toolStatus = (params: any, t: (key: string) => string) => {
  const { tool_status: toolStatusValue } = params;
  let badgeColor = BadgeColor.GRAY;
  let badgeLabel = toolStatusValue;

  if (toolStatusValue === 'DRAFT') {
    badgeColor = BadgeColor.YELLOW;
    badgeLabel = t('tool_status_draft');
  } else if (toolStatusValue === 'ACTIVE') {
    badgeColor = BadgeColor.GREEN;
    badgeLabel = t('tool_status_active');
  } else if (toolStatusValue === 'DISABLED') {
    badgeLabel = t('tool_status_disabled');
  } else if (toolStatusValue === 'STALE') {
    badgeColor = BadgeColor.YELLOW;
    badgeLabel = t('tool_status_stale');
  } else if (toolStatusValue === 'NOT_AVAILABLE') {
    badgeLabel = t('tool_status_not_available');
  } else if (toolStatusValue === 'PENDING') {
    badgeLabel = t('tool_status_pending');
  } else if (toolStatusValue === 'DEPLOYING') {
    badgeLabel = t('tool_status_deploying');
  } else if (toolStatusValue === 'UNDEPLOYING') {
    badgeLabel = t('tool_status_undeploying');
  } else if (toolStatusValue === 'DISABLING') {
    badgeLabel = t('tool_status_disabling');
  } else if (toolStatusValue === 'ENABLING') {
    badgeLabel = t('tool_status_enabling');
  } else if (toolStatusValue === 'FAILED') {
    badgeColor = BadgeColor.RED;
    badgeLabel = t('tool_status_failed');
  }
  return (
    <ExBadge
      color={badgeColor}
      shape={BadgeShape.SQUARED}
      size={BadgeSize.SMALL}
    >
      {badgeLabel}
    </ExBadge>
  );
};
export const renderSourceName = (
  sourceName: string,
  handleClick: any,
) => (
  <div>
    <div className="source-name-container">
      <ExTooltip
        tooltipDelay={0}
        position={TooltipPosition.TOP}
        alignment={TooltipAlignment.MIDDLE}
      >
        <div
          style={{
            display: 'flex',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '300px',
          }}
        >
          {sourceName}
        </div>
        <button
          slot="anchor"
          type="button"
          data-testid="sources-list-source-name-cell"
          data-locator-id="sources-list-source-name-cell"
          onClick={(e) => {
            e.preventDefault(); // Prevent default anchor navigation
            handleClick();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            paddingLeft: 0,
            color: 'var(--exo-color-font-link)',
            cursor: 'pointer',
            fontSize: 'var(--exo-font-size-small)',
            fontWeight: 400,
            lineHeight: '15px',
            paddingBottom: '6px',
            paddingTop: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '300px',
            textDecoration: 'underline',
            fontFamily: 'inherit',
          }}
        >
          {sourceName}
        </button>
      </ExTooltip>
    </div>
  </div>
);
export const renderImportedToolCountForSource = (
  hasAgentEditPermission: boolean,
  importedTools: number,
  handleClick: any,
  sourceId: string,
  sourceName: string,
) => (
  <div>
    <div className="imported-tool-count-container">
      <div>
        {hasAgentEditPermission && (
          <ExTooltip
            tooltipDelay={0}
            position={TooltipPosition.TOP}
            alignment={TooltipAlignment.MIDDLE}
          >
            <div className="imported-tool-count-tooltip-content">
              {' '}
              {importedTools}
              {' '}
            </div>
            {importedTools > 0 ? (
              <button
                slot="anchor"
                type="button"
                data-testid="tools-count-cell"
                data-locator-id="tools-count-cell"
                onClick={(e) => {
                  e.preventDefault(); // Prevent default anchor navigation
                  handleClick(sourceId, sourceName);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(sourceId, sourceName);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  paddingLeft: 0,
                  color: 'var(--exo-color-font-link)',
                  cursor: 'pointer',
                  fontSize: 'var(--exo-font-size-small)',
                  fontWeight: 400,
                  lineHeight: '15px',
                  paddingBottom: '6px',
                  paddingTop: '4px',
                  textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
                className="imported-tool-count-clickable"
              >
                {importedTools}
              </button>
            ) : (
              <div
                slot="anchor"
                data-testid="tools-count-cell"
                data-locator-id="tools-count-cell"
                style={{
                  fontWeight: 400,
                  lineHeight: '15px',
                  paddingBottom: '6px',
                  paddingTop: '4px',
                }}
              >
                {importedTools}
              </div>
            )}
          </ExTooltip>
        )}
        {!hasAgentEditPermission && <div>{importedTools}</div>}
      </div>
    </div>
  </div>
);
export const renderToolNameForSource = (
  hasAgentEditPermission: boolean,
  toolName: string,
  handleClick: any,
) => (
  <div>
    <div className="tool-name-container">
      <div>
        {hasAgentEditPermission && (
          <ExTooltip
            tooltipDelay={0}
            position={TooltipPosition.TOP}
            alignment={TooltipAlignment.MIDDLE}
          >
            <div className="tool-name-tooltip-content">
              {' '}
              {toolName}
              {' '}
            </div>
            <div
              slot="anchor"
              role="button"
              data-testid="tools-list-tool-name-cell"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault(); // Prevent default anchor navigation
                handleClick();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
              className="tool-name-clickable"
              style={{
                color: 'var(--exo-color-font-link)',
                cursor: 'pointer',
                fontWeight: 400,
                lineHeight: '15px',
                paddingBottom: '6px',
                paddingTop: '4px',
              }}
            >
              {toolName}
            </div>
          </ExTooltip>
        )}
        {!hasAgentEditPermission && <div>{toolName}</div>}
      </div>
    </div>
  </div>
);

// Clamps an ExTooltip anchor's rect to the visible ag-grid viewport so the tooltip stays in view
export const clampTooltipAnchorToGridViewport = (el: HTMLElement | null) => {
  if (!el || Object.hasOwn(el, 'getBoundingClientRect')) return;
  const getAnchorRect = el.getBoundingClientRect.bind(el);
  el.getBoundingClientRect = () => {
    const rect = getAnchorRect();
    const viewport = el.closest('.ag-center-cols-viewport');
    if (!viewport) return rect;
    const viewportRect = viewport.getBoundingClientRect();
    const left = Math.max(rect.left, viewportRect.left);
    const right = Math.max(left, Math.min(rect.right, viewportRect.right));
    return new DOMRect(left, rect.top, right - left, rect.height);
  };
};

/**
 * Formats source type for display
 * @param sourceType - The source type from API response
 * @returns The formatted source type for display
 */
export const formatSourceType = (sourceType: string): string => {
  if (sourceType === 'acp_source') {
    return 'API Control Plane';
  }
  return sourceType;
};

const getStatusSpecificItems = (
  permissions: any,
  status: string,
  onItemSelect: (value: string) => void,
  t: any,
) => {
  if (status === 'STALE' || status === 'ACTIVE') {
    return permissions.hasAgentInstallPermission ? (
      <ExMenuItem
        value="disable"
        onClick={() => onItemSelect('deactivate')}
        data-testid="sources-list-action-disable"
      >
        {t('source_disable')}
      </ExMenuItem>
    ) : null;
  }

  if (status === 'DISABLED') {
    return permissions.hasAgentInstallPermission ? (
      <ExMenuItem
        value="activate"
        data-testid="sources-list-action-enable"
        onClick={() => {
          onItemSelect('activate');
        }}
      >
        {t('source_enable')}
      </ExMenuItem>
    ) : null;
  }

  return null;
};
const getActionMenuItems = (
  permissions: any,
  status: string,
  onItemSelect: (value: string) => void,
  t: (key: string) => string,
  showEditMenuItem: boolean = true,
) => {
  const statusSpecificItems = getStatusSpecificItems(
    permissions,
    status,
    onItemSelect,
    t,
  );

  const menuItems = (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {permissions.hasAgentViewPermission && (
        <ExMenuItem
          value="viewDetails"
          onClick={() => onItemSelect('viewDetails')}
          data-testid="sources-list-action-view-details"
          data-locator-id="sources-list-action-view-details"
        >
          {t('view_details')}
        </ExMenuItem>
      )}

      {permissions.hasAgentCreatePermission && (
        <>
          {showEditMenuItem && (
            <ExMenuItem
              value="edit"
              onClick={() => onItemSelect('edit')}
              data-testid="sources-list-action-edit"
              data-locator-id="sources-list-action-edit"
            >
              {t('source_edit')}
            </ExMenuItem>
          )}
          {statusSpecificItems}
          <ExMenuItem
            value="duplicate"
            variant={MenuItemVariant.RISKY}
            onClick={() => onItemSelect('delete')}
            data-testid="sources-list-action-delete"
            data-locator-id="sources-list-action-delete"
          >
            {t('source_delete')}
          </ExMenuItem>
        </>
      )}
    </>
  );
  return (
    menuItems
  );
};
export const handleEditAction = (id: string, navigate: any, sourceType?: string) => {
  if (sourceType === 'ACP' || sourceType === 'acp_source') {
    navigate(`/add-acp-source?source=${id}`);
  } else {
    navigate(`/add-source?source=${id}&mode=edit`);
  }
};

const handleViewDetailsAction = (
  id: string,
  setSelectedSourceId: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  setSelectedSourceId(id);
};

export function SourcesActionCellRenderer(
  permissions: any,
  params: any,
  { t, navigate }: { t: any, navigate: any },
  setSelectedSourceId: any,
  // eslint-disable-next-line
  openDropdownRef: any,
  onActionWithConfirmation?: (id: string, action: string,
    agentsEquippedCount: number, sourceType?: string) => void,
  showEditMenuItem: boolean = true,
) {
  const {
    id, status, agents_equipped: agentsEquippedCount, type: sourceType,
  } = params;

  const handleActionWithConfirmation = (
    sourceId: string,
    action: string,
    agentsEqpCount: number,
  ) => {
    if (onActionWithConfirmation) {
      onActionWithConfirmation(sourceId, action, agentsEqpCount, sourceType);
    }
  };

  const actionHandlers: { [key: string]: () => void } = {
    viewDetails: () => handleViewDetailsAction(id, setSelectedSourceId),
    edit: () => handleEditAction(id, navigate, sourceType),
    delete: () => handleActionWithConfirmation(id, 'delete', agentsEquippedCount),
    deactivate: () => handleActionWithConfirmation(id, 'deactivate', agentsEquippedCount),
    activate: () => handleActionWithConfirmation(id, 'activate', agentsEquippedCount),
  };
  const handleItemSelect = (value: string) => {
    if (actionHandlers[value]) {
      actionHandlers[value]();
    } else {
      console.warn(`Unknown action: ${value}`);
    }
  };

  return (
    <div className="sources-action-container">
      <ExDropdown
        ref={dropdownRefHandler(openDropdownRef)}
        icon="More options"
        text=""
        keepOpen={false}
        flavor={ButtonFlavor.BRANDED}
        type={ButtonType.TERTIARY}
      >
        <ExMenu className="tool-action-menu">
          {getActionMenuItems(permissions, status, handleItemSelect, t, showEditMenuItem)}
        </ExMenu>
      </ExDropdown>
    </div>
  );
}
